/**
 * Stock Sentry Dashboard - Main App Route
 * Phase 3 - Displays alerts from velocity calculations
 *
 * Refactored: Extracted components, hooks, and services for maintainability
 */

import { useLoaderData, useRevalidator, useSearchParams, useNavigation } from 'react-router';
import {
  Page,
  Banner,
  Text,
  BlockStack,
  InlineStack,
  Button,
  Layout,
  Spinner,
  Tooltip,
  Badge,
} from '@shopify/polaris';
import {
  SettingsIcon,
  QuestionCircleIcon,
} from '@shopify/polaris-icons';
import { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useToastHelpers } from '../components/feedback/ToastProvider';
import {
  AsyncSection,
  LoadingOverlay,
  SkeletonChart,
} from '../components/loading';
import {
  parseURLFilters,
  serializeFiltersToURL,
  formatRangeLabel,
  type AppliedFilter,
} from '../types/search';
import { OnboardingWizard, type WizardProduct } from '../components/onboarding/OnboardingWizard';

// Hooks - organized by domain
import { useLoading } from '../hooks/shopify';
import {
  useDashboardTabs,
  useDashboardFilters,
  useDashboardUIState,
  useDashboardStatistics,
  useDeepLinking,
  useAutocompleteSuggestions,
} from '../hooks/dashboard';
import { useAlertActions, useAlertFiltering, useExportAlerts } from '../hooks/alerts';
import { useProductFilters, useProductNavigation, useProductManagement } from '../hooks/products';
import { useRangeFilters } from '../hooks/filters';
import { useOnboardingState, useTestData } from '../hooks/onboarding';
import { AnalyticsCard } from '../components/dashboard/AnalyticsCard';
import { DashboardHeader, type StatTrends } from '../components/dashboard/DashboardHeader';
import { ShopNotFound } from '../components/error/ShopNotFound';

// Import extracted Dashboard components
import {
  DashboardSkeleton,
  DashboardErrorBanners,
  MonitoringCenter,
  DashboardModals,
} from '../components/dashboard';

// Import modal context provider and hooks
import {
  DashboardModalProvider,
  useAlertDetailModal,
} from '../contexts/DashboardModalContext';

// Import types from shared definitions
import type {
  EnhancedAlert,
  LoaderData,
} from '../types/dashboard';

// Re-export loader from server file (extracted for maintainability)
export { loader } from '../loaders/dashboard.server';

/**
 * MonitoringCenterConnected
 * 
 * Wrapper component that connects MonitoringCenter to the DashboardModalContext.
 * This allows MonitoringCenter to trigger alert detail modal via context.
 */
function MonitoringCenterConnected(props: Omit<React.ComponentProps<typeof MonitoringCenter>, 'onAlertClick'>) {
  const alertDetailModal = useAlertDetailModal();
  return <MonitoringCenter {...props} onAlertClick={alertDetailModal.open} />;
}

/**
 * Dashboard Component
 */
export default function Dashboard() {
  const { alerts, alertHistory, onboardingData, trackedProductsCount, trackedProducts, trackedVariantIds, alertTrendsData, velocityAnalytics, inventoryTrends, notificationChannels, error, shopNotFound, shopDomain } = useLoaderData<LoaderData>();
  const revalidator = useRevalidator();
  const [searchParams] = useSearchParams();
  const navigation = useNavigation();
  const toast = useToastHelpers();
  const stockAlertsRef = useRef<HTMLDivElement>(null);

  // UI state for modals, menus, error banner, and refresh using extracted hook
  const {
    addProductModalOpen,
    setAddProductModalOpen,
    isSearchExpanded,
    setIsSearchExpanded,
    isActionsMenuOpen,
    setIsActionsMenuOpen,
    errorBanner,
    setErrorBanner,
    isRefreshing,
    handleRefresh,
  } = useDashboardUIState();

  // Deep linking from Shopify Admin Action extension
  const {
    preSelectedProductId,
    shouldOpenAddProductModal,
    clearDeepLink,
  } = useDeepLinking();

  // Pagination for Monitored Products tab
  const [monitoredProductsPage, setMonitoredProductsPage] = useState(1);
  const monitoredProductsPerPage = 5;

  // Use extracted hooks for cleaner code
  const {
    optimisticAlerts,
    dismissingAlerts,
    resolvingAlerts,
    notifyingAlerts,
    modalState: alertModalState,
    openResolveConfirmation,
    openDismissConfirmation,
    openNotifyConfirmation,
    handleConfirmedResolve,
    handleConfirmedDismiss,
    handleConfirmedNotify,
    handleChannelSelectionChange,
    closeModals: closeAlertModals,
    onAlertResolved,
    onAlertDismissed,
    onAlertNotified,
  } = useAlertActions(alerts, notificationChannels);

  const {
    exportState,
    openExportModal,
    closeExportModal,
    setExportFormat,
    setExportFields,
    handleExport,
  } = useExportAlerts();

  // Count alerts for tab badges (must be after useAlertActions)
  const activeAlertsCount = optimisticAlerts.filter(a => a.severity === 'critical' || a.severity === 'warning').length;

  // Use dashboard tabs hook for tab state management
  const {
    selectedTab,
    tabs,
    isTabSwitching,
    handleTabChange,
    handleStatisticsCardClick,
  } = useDashboardTabs({
    activeAlertsCount,
    monitoredProductsCount: trackedProductsCount,
  });

  // Unified filter state management using facade hook
  // Handles search, severity, range, and product filters with URL synchronization
  const {
    searchQuery,
    setSearchQuery,
    debouncedSearchQuery,
    severityFilter,
    setSeverityFilter,
    rangeFilters,
    productFilters,
    appliedFilterPills,
    totalFilterCount,
    handleClearAllFilters,
    handleRemoveFilterPill,
    handleAlertFiltersClearAll,
  } = useDashboardFilters({
    products: trackedProducts,
    selectedTab,
  });

  // Destructure range filter values for component props
  const {
    daysMin,
    daysMax,
    inventoryMin,
    inventoryMax,
    daysRangeError,
    inventoryRangeError,
    setDaysMin,
    setDaysMax,
    setInventoryMin,
    setInventoryMax,
    handleDaysMinChange,
    handleDaysMaxChange,
    handleInventoryMinChange,
    handleInventoryMaxChange,
  } = rangeFilters;

  // Destructure product filter values for component props
  const {
    filteredProducts,
    filterOptions,
    setStatusFilter,
    setProductTypeFilter,
    setProductVendorFilter,
  } = productFilters;

  // Onboarding and wizard state management
  const onboarding = useOnboardingState({
    initialData: onboardingData,
    hasError: !!error,
    revalidate: revalidator.revalidate,
    toast,
    onSwitchToMonitoredProducts: () => handleStatisticsCardClick(1, stockAlertsRef),
  });

  // Product navigation confirmation modal using extracted hook
  const {
    modalState: navigationModal,
    openNavigationConfirmation,
    handleConfirmedNavigation,
  } = useProductNavigation();

  // Note: alertDetailModal state is now managed in DashboardModalContext
  // MonitoringCenter receives openAlertDetail from context via MonitoringCenterConnected

  // Use extracted hook for test data management
  const testData = useTestData({
    onComplete: () => {
      toast.success('Test data generated successfully');
      revalidator.revalidate();
    },
    onError: (error) => {
      setErrorBanner(error);
    },
    toast,
    revalidate: () => revalidator.revalidate(),
  });

  // Page loading state
  const isPageLoading = navigation.state === 'loading';

  // Control App Bridge loading indicator
  useLoading(isPageLoading);

  // Preserve query parameters for navigation
  const queryString = searchParams.toString();
  const settingsUrl = `/app/settings${queryString ? `?${queryString}` : ''}`;
  const helpCenterUrl = `/app/help${queryString ? `?${queryString}` : ''}`;

  // Build applied filters for Active Alerts (for ResourceList component)
  const appliedAlertFilters: AppliedFilter[] = useMemo(() => {
    const filters: AppliedFilter[] = [];
    if (severityFilter.length > 0) {
      filters.push({
        key: 'severity',
        label: `Severity: ${severityFilter.map(s => s.charAt(0).toUpperCase() + s.slice(1)).join(', ')}`,
        onRemove: () => setSeverityFilter([]),
      });
    }
    if (daysMin || daysMax) {
      filters.push({
        key: 'daysUntilStockout',
        label: `Stockout: ${formatRangeLabel(daysMin, daysMax, 'days')}`,
        onRemove: () => { setDaysMin(''); setDaysMax(''); },
      });
    }
    if (inventoryMin || inventoryMax) {
      filters.push({
        key: 'inventoryLevel',
        label: `Inventory: ${formatRangeLabel(inventoryMin, inventoryMax, 'units')}`,
        onRemove: () => { setInventoryMin(''); setInventoryMax(''); },
      });
    }
    return filters;
  }, [severityFilter, daysMin, daysMax, inventoryMin, inventoryMax, setSeverityFilter, setDaysMin, setDaysMax, setInventoryMin, setInventoryMax]);

  // Filter alerts and history using extracted hook
  const { filteredAlerts, filteredHistory } = useAlertFiltering({
    alerts: optimisticAlerts,
    alertHistory,
    searchQuery: debouncedSearchQuery,
    severityFilter,
    daysMin,
    daysMax,
    inventoryMin,
    inventoryMax,
    selectedTab,
  });

  // Calculate paginated products
  const totalMonitoredProductsPages = Math.ceil(filteredProducts.length / monitoredProductsPerPage);

  // Reset pagination when filters change
  // Use JSON.stringify for array dependencies to avoid reference comparison issues
  const statusFilterKey = JSON.stringify(productFilters.filters.statusFilter);
  const productTypeFilterKey = JSON.stringify(productFilters.filters.productTypeFilter);
  const productVendorFilterKey = JSON.stringify(productFilters.filters.productVendorFilter);
  
  useEffect(() => {
    setMonitoredProductsPage(1);
  }, [searchQuery, statusFilterKey, productTypeFilterKey, productVendorFilterKey]);

  // Autocomplete suggestions for SearchFilterOverlay
  const { suggestions: autocompleteSuggestions } = useAutocompleteSuggestions({
    trackedProducts,
    alerts,
  });

  // Handle deep linking from Shopify Admin Action extension
  // The useDeepLinking hook detects URL params and sets shouldOpenAddProductModal
  useEffect(() => {
    if (shouldOpenAddProductModal && !addProductModalOpen) {
      setAddProductModalOpen(true);
      clearDeepLink();
    }
  }, [shouldOpenAddProductModal, addProductModalOpen, setAddProductModalOpen, clearDeepLink]);

  // Test data - enhanced handlePopulateTestData clears error banner
  const handlePopulateTestData = useCallback(() => {
    setErrorBanner(null);
    testData.startGeneration();
  }, [testData]);

  // Fetch test data status on mount
  useEffect(() => {
    testData.fetchStatus();
  }, []); // Only run once on mount

  // Product management using extracted hook
  const { handleSaveProducts } = useProductManagement({
    onSuccess: () => {
      setAddProductModalOpen(false);
      handleStatisticsCardClick(1, stockAlertsRef);
    },
  });

  // Calculate dashboard statistics and trends using extracted hook
  const { statistics, trends } = useDashboardStatistics({
    alerts: optimisticAlerts,
    trackedProductsCount,
    alertTrendsData,
  });

  // Show ShopNotFound component if shop not in database
  if (shopNotFound && shopDomain) {
    return <ShopNotFound shopDomain={shopDomain} />;
  }

  // Show skeleton page while loading
  if (isPageLoading) {
    return <DashboardSkeleton />;
  }

  return (
    <>
      {onboarding.shouldShowOnboarding ? (
        <div className="page-enter" style={{ backgroundColor: '#f4f4f6', minHeight: '100vh', paddingTop: '40px' }}>
          <OnboardingWizard
            products={onboarding.products}
            onComplete={onboarding.handleComplete}
            onSkip={onboarding.handleDismiss}
          />
        </div>
      ) : (
        <DashboardModalProvider
          // Shared data from loader
          trackedVariantIds={trackedVariantIds}
          notificationChannels={notificationChannels}
          shopId={onboardingData?.shopId || 0}
          hasTestData={testData.status?.hasTestData || false}
          tabs={tabs}
          selectedTab={selectedTab}
          filteredAlertsCount={filteredAlerts.length}
          filteredProductsCount={filteredProducts.length}
          // Test data counts
          testProductCount={testData.status?.counts?.trackedProducts || 0}
          testAlertCount={testData.status?.counts?.alerts || 0}
          testVelocityCount={testData.status?.counts?.velocityRecords || 0}
          // Export callbacks
          onExport={() => handleExport(selectedTab, {
            alertsCount: filteredAlerts.length,
            productsCount: filteredProducts.length,
          })}
          setExportFormat={setExportFormat}
          setExportFields={setExportFields}
          exportFormat={exportState.format}
          exportFields={exportState.fields}
          isExporting={exportState.isExporting}
          // Product management
          onSaveProducts={handleSaveProducts}
          // Alert action callbacks from useAlertActions hook
          openResolveConfirmation={openResolveConfirmation}
          openDismissConfirmation={openDismissConfirmation}
          openNotifyConfirmation={openNotifyConfirmation}
          closeAlertModals={closeAlertModals}
          onAlertResolved={onAlertResolved}
          onAlertDismissed={onAlertDismissed}
          onAlertNotified={onAlertNotified}
          alertModalState={alertModalState}
          // Product settings
          onProductSettingsSaved={() => revalidator.revalidate()}
          // Test data callbacks
          onConfirmTestRemoval={testData.confirmRemoval}
          isDeleting={testData.isDeleting}
          // Deep linking
          preSelectedProductId={preSelectedProductId}
          clearDeepLink={clearDeepLink}
        >
          <div className="page-enter" style={{ backgroundColor: '#f4f4f6', minHeight: '100vh', position: 'relative' }}>
            {/* Page background - top-left wide beams */}
            <div className="page-bg-dashboard" aria-hidden="true" />

            <div style={{ position: 'relative', zIndex: 1 }}>
              <Page
                title="Dashboard"
                subtitle="Monitor your inventory levels and stock alerts"
                primaryAction={{
                  content: 'Help Center',
                  url: helpCenterUrl,
                  icon: QuestionCircleIcon,
                }}
                secondaryActions={[
                  {
                    content: 'Settings',
                    url: onboarding.isOnboardingComplete ? settingsUrl : undefined,
                    disabled: !onboarding.isOnboardingComplete,
                    helpText: !onboarding.isOnboardingComplete ? 'Complete onboarding to access settings' : undefined,
                    icon: SettingsIcon,
                  },
                ]}
              >
                <BlockStack gap="500">
                  {/* Error banners */}
                  <DashboardErrorBanners
                    error={error}
                    errorBanner={errorBanner}
                    onDismissErrorBanner={() => setErrorBanner(null)}
                    isLoadingProducts={onboarding.isLoadingProducts}
                    testDataStatus={testData.status}
                    onRemoveTestData={testData.openRemovalModal}
                  />

                  {/* Dashboard Header with Logo and Stat Pills */}
                  {!error && onboarding.isOnboardingComplete && (
                    <div className="slide-in-up">
                      <DashboardHeader
                        statistics={statistics}
                        trends={trends}
                        onStatClick={handleStatisticsCardClick}
                        scrollRef={stockAlertsRef}
                      />
                    </div>
                  )}

                  {/* Analytics Section */}
                  {!error && onboarding.isOnboardingComplete && (
                    <div className="chart-enter">
                      <AsyncSection
                        isLoading={false}
                        isRefreshing={isRefreshing && revalidator.state === 'loading'}
                        skeleton={<SkeletonChart />}
                        refreshMessage="Updating chart..."
                        animateContent={false}
                      >
                        <AnalyticsCard
                          initialAlertTrends={alertTrendsData}
                          initialVelocityData={velocityAnalytics}
                          initialInventoryTrends={inventoryTrends}
                        />
                      </AsyncSection>
                    </div>
                  )}

                  {/* Stock Alerts Section */}
                  {!error && onboarding.isOnboardingComplete && (
                    <MonitoringCenterConnected
                      scrollRef={stockAlertsRef}
                      selectedTab={selectedTab}
                      tabs={tabs}
                      isTabSwitching={isTabSwitching}
                      onTabChange={handleTabChange}
                      isSearchExpanded={isSearchExpanded}
                      onSearchExpandChange={setIsSearchExpanded}
                      searchQuery={searchQuery}
                      onSearchChange={setSearchQuery}
                      autocompleteSuggestions={autocompleteSuggestions}
                      severityFilter={severityFilter}
                      onSeverityChange={setSeverityFilter}
                      daysMin={daysMin}
                      daysMax={daysMax}
                      onDaysMinChange={handleDaysMinChange}
                      onDaysMaxChange={handleDaysMaxChange}
                      daysRangeError={daysRangeError}
                      inventoryMin={inventoryMin}
                      inventoryMax={inventoryMax}
                      onInventoryMinChange={handleInventoryMinChange}
                      onInventoryMaxChange={handleInventoryMaxChange}
                      inventoryRangeError={inventoryRangeError}
                      statusFilter={productFilters.filters.statusFilter}
                      onStatusChange={setStatusFilter}
                      productTypeFilter={productFilters.filters.productTypeFilter}
                      onProductTypeChange={setProductTypeFilter}
                      productVendorFilter={productFilters.filters.productVendorFilter}
                      onProductVendorChange={setProductVendorFilter}
                      productTypeOptions={filterOptions.productTypeOptions}
                      productVendorOptions={filterOptions.productVendorOptions}
                      onClearAllFilters={handleClearAllFilters}
                      totalFilterCount={totalFilterCount}
                      appliedFilterPills={appliedFilterPills}
                      onRemoveFilterPill={handleRemoveFilterPill}
                      isActionsMenuOpen={isActionsMenuOpen}
                      onActionsMenuToggle={() => setIsActionsMenuOpen(!isActionsMenuOpen)}
                      onActionsMenuClose={() => setIsActionsMenuOpen(false)}
                      isRefreshing={isRefreshing}
                      onRefresh={handleRefresh}
                      onExport={openExportModal}
                      isExporting={exportState.isExporting}
                      onOpenWizard={onboarding.handleOpenWizard}
                      filteredAlerts={filteredAlerts}
                      activeAlertsCount={activeAlertsCount}
                      appliedAlertFilters={appliedAlertFilters}
                      dismissingAlerts={dismissingAlerts}
                      resolvingAlerts={resolvingAlerts}
                      notifyingAlerts={notifyingAlerts}
                      onResolve={openResolveConfirmation}
                      onDismiss={openDismissConfirmation}
                      onNotify={openNotifyConfirmation}
                      hasNotificationChannels={notificationChannels.filter(c => c.enabled && c.is_verified).length > 0}
                      onAlertFiltersClearAll={handleAlertFiltersClearAll}
                      onPopulateTestData={handlePopulateTestData}
                      isGeneratingTestData={testData.isGenerating}
                      filteredProducts={filteredProducts}
                      onboardingData={onboardingData}
                      shopDomain={searchParams.get('shop') || ''}
                      onProductClick={openNavigationConfirmation}
                      currentPage={monitoredProductsPage}
                      totalPages={totalMonitoredProductsPages}
                      itemsPerPage={monitoredProductsPerPage}
                      onPageChange={setMonitoredProductsPage}
                      filteredHistory={filteredHistory}
                      hasTestData={testData.status?.hasTestData}
                    />
                  )}
                </BlockStack>
              </Page>
            </div>
          </div>

          {/* All Dashboard Modals - rendered inside provider so they can access context */}
          <DashboardModals />
        </DashboardModalProvider>
      )}
    </>
  );
}
